// ─────────────────────────────────────────────────────────────────────────────
// Tokens (paleta + tipografia Ciclo)
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  bg: "#F5F2EA",
  surface: "#FFFFFF",
  border: "#E5E1D5",
  borderStrong: "#D6D1C2",
  text: "#1A1A1A",
  textMuted: "#5C5C5C",
  textSoft: "#8A8A82",

  carriers: ["#D04638", "#E08A1E", "#2A75B0", "#2C8D5E", "#7A4FB1", "#B83B6B"],
  carriersSoft: ["#FBE8E5", "#FBEFD9", "#E2EDF7", "#DEEEE5", "#EBE2F4", "#F8DDE7"],

  good: "#1F8A4F",
  goodSoft: "#E2F1E8",
  bad: "#C53D2E",
  badSoft: "#FBE5E2",
  neutral: "#6B6B6B",
  neutralSoft: "#ECEAE2",
  warn: "#A87320",
  warnSoft: "#FBEFD9",
};

export const fonts = {
  body: "'Geist', -apple-system, sans-serif",
  mono: "'Geist Mono', monospace",
};

export const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  @keyframes shipping-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(44, 141, 94, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(44, 141, 94, 0); }
    100% { box-shadow: 0 0 0 0 rgba(44, 141, 94, 0); }
  }

  @keyframes shipping-spin {
    to { transform: rotate(360deg); }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper
// ─────────────────────────────────────────────────────────────────────────────
export const page = {
  background: colors.bg,
  minHeight: "100vh",
  padding: "24px 32px 28px",
  fontFamily: fonts.body,
  color: colors.text,
  fontFeatureSettings: "'tnum' 1, 'cv11' 1, 'ss01' 1",
  WebkitFontSmoothing: "antialiased",
};

export const dash = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
  maxWidth: 1920,
  margin: "0 auto",
};

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────
export const header = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 2,
    flexWrap: "wrap",
    gap: 16,
  },
  title: {
    fontSize: 38,
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1,
    margin: 0,
  },
  subtitle: {
    fontSize: 15,
    color: colors.textMuted,
    letterSpacing: "0.01em",
    marginTop: 8,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  pill: (loading) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 16px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: colors.textMuted,
    cursor: loading ? "default" : "pointer",
    userSelect: "none",
  }),
  pillDot: (loading) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: loading ? colors.carriers[1] : colors.carriers[3],
    animation: loading ? "shipping-pulse 1.2s infinite" : "shipping-pulse 1.8s infinite",
  }),
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: colors.bad,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Uploads
// ─────────────────────────────────────────────────────────────────────────────
export const uploads = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "22px 26px 24px",
  },
  locked: {
    background: colors.surface,
    border: `1px dashed ${colors.borderStrong}`,
    borderRadius: 18,
    padding: "28px 26px",
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.02em",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 10,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  sub: {
    fontSize: 12,
    color: colors.textMuted,
    letterSpacing: "0.02em",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 14,
  },
  card: (idx, hasData) => ({
    position: "relative",
    border: `1px solid ${colors.border}`,
    borderLeft: `4px solid ${colors.carriers[idx % colors.carriers.length]}`,
    background: hasData
      ? colors.carriersSoft[idx % colors.carriersSoft.length]
      : colors.surface,
    borderRadius: 12,
    padding: "14px 16px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    transition: "background 0.2s ease",
  }),
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  carrier: (idx) => ({
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: "0.06em",
    color: colors.carriers[idx % colors.carriers.length],
    margin: 0,
  }),
  badge: (state) => {
    const map = {
      empty: { bg: colors.bg, color: colors.textSoft },
      ready: { bg: colors.goodSoft, color: colors.good },
    };
    const c = map[state] || map.empty;
    return {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      padding: "3px 8px",
      borderRadius: 6,
      background: c.bg,
      color: c.color,
    };
  },
  meta: {
    fontSize: 12,
    color: colors.textMuted,
    lineHeight: 1.5,
  },
  metaStrong: {
    color: colors.text,
    fontWeight: 700,
    fontFamily: fonts.mono,
  },
  actions: {
    marginTop: 6,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  fileBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "7px 12px",
    background: colors.text,
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.05em",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  fileBtnGhost: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 10px",
    background: "transparent",
    color: colors.textMuted,
    fontSize: 12,
    fontWeight: 600,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  hint: {
    fontSize: 11,
    color: colors.textSoft,
    marginTop: 2,
    lineHeight: 1.4,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Action bar (processar / status)
// ─────────────────────────────────────────────────────────────────────────────
export const actions = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 18,
    flexWrap: "wrap",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: "14px 22px",
  },
  left: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: colors.textMuted,
  },
  value: {
    fontSize: 16,
    fontWeight: 600,
    color: colors.text,
    fontFamily: fonts.mono,
    letterSpacing: "-0.01em",
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  exportBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 14px",
    background: colors.surface,
    color: colors.text,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  process: (disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 22px",
    background: disabled ? colors.borderStrong : colors.text,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.06em",
    border: "none",
    borderRadius: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit",
  }),
  progress: {
    minWidth: 220,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  progressLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.textMuted,
  },
  progressBar: {
    height: 6,
    background: colors.bg,
    borderRadius: 999,
    overflow: "hidden",
    border: `1px solid ${colors.border}`,
  },
  progressFill: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: colors.carriers[3],
    transition: "width 0.3s ease",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// KPIs
// ─────────────────────────────────────────────────────────────────────────────
export const kpis = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  card: (idx) => ({
    position: "relative",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: "16px 18px 18px",
    borderTop: `4px solid ${colors.carriers[idx % colors.carriers.length]}`,
  }),
  label: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.textMuted,
    marginBottom: 10,
    minHeight: 30,
  },
  value: (state) => {
    const map = {
      good: colors.good,
      bad: colors.bad,
      neutral: colors.text,
    };
    return {
      fontSize: 30,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.025em",
      lineHeight: 1,
      color: state ? map[state] : colors.text,
      fontFamily: fonts.mono,
    };
  },
  sub: {
    marginTop: 8,
    fontSize: 12,
    color: colors.textMuted,
    lineHeight: 1.4,
  },
  pill: (state) => {
    const map = {
      good: { bg: colors.goodSoft, color: colors.good },
      bad: { bg: colors.badSoft, color: colors.bad },
      neutral: { bg: colors.neutralSoft, color: colors.neutral },
    };
    const c = map[state] || map.neutral;
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      marginTop: 8,
      padding: "3px 8px",
      borderRadius: 5,
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: "0.04em",
      background: c.bg,
      color: c.color,
    };
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Tabela por transportadora / canal
// ─────────────────────────────────────────────────────────────────────────────
export const breakdown = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "20px 24px 22px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 14,
    gap: 12,
    flexWrap: "wrap",
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  sub: {
    fontSize: 12,
    color: colors.textMuted,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: fonts.body,
  },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: colors.textMuted,
    padding: "10px 12px",
    borderBottom: `1px solid ${colors.border}`,
    textTransform: "uppercase",
  },
  thRight: {
    textAlign: "right",
  },
  td: {
    fontSize: 13,
    color: colors.text,
    padding: "12px 12px",
    borderBottom: `1px solid ${colors.border}`,
    fontVariantNumeric: "tabular-nums",
  },
  tdRight: {
    textAlign: "right",
    fontFamily: fonts.mono,
    fontWeight: 600,
  },
  tdBad: { color: colors.bad },
  tdGood: { color: colors.good },
  carrierTag: (idx) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    color: colors.text,
    fontSize: 13,
  }),
  swatch: (bg) => ({
    width: 10,
    height: 10,
    borderRadius: 3,
    background: bg,
    flexShrink: 0,
  }),
  totalRow: {
    fontWeight: 700,
    background: colors.bg,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Linhas de detalhe (não encontradas, etc.)
// ─────────────────────────────────────────────────────────────────────────────
export const issues = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: "16px 20px 18px",
  },
  head: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    marginBottom: 10,
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: colors.text,
    margin: 0,
  },
  sub: {
    fontSize: 12,
    color: colors.textMuted,
  },
  list: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    maxHeight: 140,
    overflowY: "auto",
  },
  chip: {
    fontSize: 11.5,
    fontFamily: fonts.mono,
    fontWeight: 600,
    background: colors.warnSoft,
    color: colors.warn,
    padding: "3px 8px",
    borderRadius: 6,
    border: `1px solid ${colors.warn}33`,
  },
  empty: {
    fontSize: 12,
    color: colors.textSoft,
    fontStyle: "italic",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Base de pedidos do mês
// ─────────────────────────────────────────────────────────────────────────────
export const baseline = {
  wrap: (done) => ({
    background: colors.surface,
    border: `1px solid ${done ? colors.border : colors.borderStrong}`,
    borderLeft: `4px solid ${done ? colors.good : colors.warn}`,
    borderRadius: 18,
    padding: "20px 26px 22px",
  }),
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    flexWrap: "wrap",
  },
  headLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
  },
  tag: (done, loading) => ({
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    marginTop: 2,
    padding: "5px 11px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: loading ? colors.warn : done ? colors.good : colors.textMuted,
    background: loading ? colors.warnSoft : done ? colors.goodSoft : colors.neutralSoft,
    animation: loading ? "shipping-pulse 1.4s infinite" : "none",
  }),
  title: {
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    color: colors.text,
    margin: 0,
  },
  sub: {
    fontSize: 12,
    color: colors.textMuted,
    letterSpacing: "0.02em",
    marginTop: 4,
    marginBottom: 0,
  },
  reloadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 14px",
    background: colors.surface,
    color: colors.text,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    flexShrink: 0,
  },
  error: {
    marginTop: 14,
    background: colors.badSoft,
    border: `1px solid ${colors.bad}`,
    borderRadius: 10,
    padding: "10px 16px",
    color: colors.bad,
    fontSize: 13,
    fontWeight: 600,
  },
  loadingRow: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 13,
    fontWeight: 600,
    color: colors.textMuted,
    fontFamily: fonts.mono,
  },
  spinner: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: `2.5px solid ${colors.border}`,
    borderTopColor: colors.warn,
    animation: "shipping-spin 0.8s linear infinite",
    flexShrink: 0,
  },
  metrics: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 14,
  },
  metric: {
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: colors.textMuted,
  },
  metricValue: {
    fontSize: 24,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
    color: colors.text,
    fontFamily: fonts.mono,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Gráficos (pizzas)
// ─────────────────────────────────────────────────────────────────────────────
export const charts = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "20px 24px 24px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 14,
    gap: 12,
    flexWrap: "wrap",
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  sub: {
    fontSize: 12,
    color: colors.textMuted,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 16,
  },
  card: (idx) => ({
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: "14px 16px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    borderTop: `3px solid ${colors.carriers[idx % colors.carriers.length]}`,
  }),
  cardHead: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginBottom: 4,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.text,
    margin: 0,
  },
  cardSub: {
    fontSize: 11,
    color: colors.textMuted,
    letterSpacing: "0.02em",
  },
  pieWrap: {
    width: "100%",
  },
  legend: {
    fontSize: 11,
    paddingTop: 6,
  },
  legendItem: {
    fontSize: 11.5,
    fontWeight: 600,
    color: colors.text,
    letterSpacing: "0.02em",
  },
  tooltip: {
    background: colors.text,
    color: "#fff",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 12,
    fontFamily: fonts.body,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  tooltipLabel: {
    fontWeight: 700,
    marginBottom: 3,
    fontSize: 11.5,
    letterSpacing: "0.02em",
  },
  tooltipValue: {
    fontFamily: fonts.mono,
    fontWeight: 600,
    fontSize: 13,
  },
  tooltipPct: {
    fontSize: 11,
    opacity: 0.75,
    marginTop: 2,
    fontFamily: fonts.mono,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    paddingTop: 8,
    borderTop: `1px dashed ${colors.borderStrong}`,
  },
  totalLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.textMuted,
  },
  totalValue: {
    fontFamily: fonts.mono,
    fontWeight: 700,
    fontSize: 14,
    color: colors.text,
  },
  empty: {
    fontSize: 12,
    color: colors.textSoft,
    fontStyle: "italic",
    padding: "30px 0",
    textAlign: "center",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────
export const footer = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 6px 0",
    fontSize: 13,
    color: colors.textMuted,
    flexWrap: "wrap",
    gap: 12,
  },
  legend: {
    display: "flex",
    gap: 18,
    flexWrap: "wrap",
    alignItems: "center",
  },
  item: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    color: colors.text,
    fontSize: 12,
    letterSpacing: "0.04em",
  },
  swatch: (bg) => ({
    width: 12,
    height: 12,
    borderRadius: 3,
    background: bg,
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
export function balanceState(saldo) {
  if (saldo == null) return "neutral";
  if (saldo > 0) return "good";
  if (saldo < 0) return "bad";
  return "neutral";
}
